/**
 * ChairPhysics - Chair physics bodies, simulation toggling and ragdoll control
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * Physics state (isPhysicsPlaying, chairPhysicsBodies, chairPhysicsMeshes,
 * savedMeshTransforms, savedCompoundBodyTransform, ragdoll) stays on the
 * Application - it is read from the animate loop and elsewhere.
 */
import * as THREE from 'three';
import { chairLocalBox } from './Ragdoll.js';

export class ChairPhysics {
    constructor(app) {
        this.app = app;
    }

    /**
     * Create single compound physics body for all chair meshes
     * More efficient than individual bodies - reduces physics overhead
     */
    createCompoundChairPhysicsBody() {
        if (this.app.generatedMeshes.length === 0) {
            console.warn('No chair meshes to create physics body for');
            return;
        }

        // The body's centre of mass and collider transforms are read straight
        // off the mesh positions, so the meshes have to be in their generated
        // frame here - otherwise a parked chair's offset gets baked into the
        // body and then applied again on top of itself
        this.app.unparkGeneratedMeshes();

        try {
            // Create single compound body from all chair meshes
            const body = this.app.physicsWorld.createCompoundDynamicBody(this.app.generatedMeshes, {
                restitution: 0.3,
                friction: 0.8,
                mass: 5.0 // Total mass for entire chair
            });

            if (body) {
                // NOTE: Don't push to chairPhysicsBodies - compound bodies are already tracked
                // in physicsWorld.compoundBodies and will be removed via clearCompoundBodies()
                // this.app.chairPhysicsBodies.push(body); // REMOVED - causes double-removal error

                // Store all meshes for reference
                this.app.chairPhysicsMeshes = [...this.app.generatedMeshes];
                console.log(`Created compound physics body for ${this.app.generatedMeshes.length} chair pieces`);

                // The body is built at the meshes' centre of mass, so a chair
                // the user parked with the hand tool would snap back here -
                // put it back where they left it
                this.app.applyChairMoveOffset();
            }
        } catch (error) {
            console.error('Failed to create compound chair physics body:', error);
        }
    }

    /**
     * Clear all chair physics bodies
     */
    clearChairPhysicsBodies() {
        console.log('🧹 Clearing chair physics bodies...');

        // Clear compound bodies first (chair assemblies)
        this.app.physicsWorld.clearCompoundBodies();

        // Clear any individual bodies
        while (this.app.chairPhysicsBodies.length > 0) {
            const body = this.app.chairPhysicsBodies.pop();
            const mesh = this.app.chairPhysicsMeshes.pop();

            if (body) {
                try {
                    this.app.physicsWorld.world.removeRigidBody(body);
                    // Also remove from PhysicsWorld's mesh-body mapping
                    if (mesh) {
                        this.app.physicsWorld.bodies.delete(mesh);
                    }
                } catch (error) {
                    console.warn('Failed to remove chair physics body:', error);
                }
            }
        }

        // Clear mesh references
        this.app.chairPhysicsMeshes = [];

        console.log('✅ Finished clearing all chair physics bodies');
    }

    /**
     * Toggle physics simulation on/off
     * @param {boolean} play - True to play, false to pause
     */
    togglePhysics(play) {
        if (play) {
            // Save current positions before starting physics
            this.savePhysicsPositions();

            // Start tracking the chair's frame delta so the sketch overlay
            // and pointer raycasting follow the simulated chair. Stays
            // active while paused (meshes keep their simulated pose) and is
            // cleared on reset/restore.
            this.app.physicsOverlayActive = true;
        }
        this.app.isPhysicsPlaying = play;
        console.log('Physics simulation:', play ? 'playing' : 'paused');
    }

    /**
     * Save current mesh positions/rotations before physics simulation
     */
    savePhysicsPositions() {
        this.app.savedMeshTransforms = [];

        // Save positions of all generated meshes
        this.app.generatedMeshes.forEach(mesh => {
            this.app.savedMeshTransforms.push({
                mesh: mesh,
                position: mesh.position.clone(),
                quaternion: mesh.quaternion.clone(),
                rotation: mesh.rotation.clone()
            });
        });

        // Save compound body state if it exists
        if (this.app.physicsWorld && this.app.physicsWorld.compoundBodies.length > 0) {
            const compoundData = this.app.physicsWorld.compoundBodies[0]; // Assume first compound body is the chair
            if (compoundData && compoundData.body) {
                const translation = compoundData.body.translation();
                const rotation = compoundData.body.rotation();
                this.app.savedCompoundBodyTransform = {
                    position: new THREE.Vector3(translation.x, translation.y, translation.z),
                    quaternion: new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w)
                };
            }
        }

        console.log(`Saved positions for ${this.app.savedMeshTransforms.length} meshes`);
    }

    /**
     * Restore mesh positions/rotations to pre-physics state
     */
    restorePhysicsPositions() {
        // Restore compound body first (if saved)
        if (this.app.savedCompoundBodyTransform && this.app.physicsWorld) {
            this.app.physicsWorld.resetCompoundBodyTransform(
                this.app.savedCompoundBodyTransform.position,
                this.app.savedCompoundBodyTransform.quaternion
            );
            console.log('Restored compound body to saved position');
        } else if (this.app.physicsWorld) {
            // No pre-play snapshot exists when the chair was only hand-dragged
            // while paused. Send the body back to its creation pose (plus any
            // hand-tool move, re-applied below) - otherwise the meshes stay
            // where the simulation left them and desync from the sketch.
            this.app.physicsWorld.resetCompoundBodiesToCreationPose();
            console.log('Reset compound body to its creation pose');
        }

        // Then restore individual mesh positions
        // (compound body sync will override these, but we restore them anyway for consistency)
        this.app.savedMeshTransforms.forEach(saved => {
            saved.mesh.position.copy(saved.position);
            saved.mesh.quaternion.copy(saved.quaternion);
            saved.mesh.rotation.copy(saved.rotation);

            // Also update individual physics bodies (if they exist and are not part of compound)
            if (this.app.physicsWorld && this.app.physicsWorld.getBody(saved.mesh)) {
                this.app.physicsWorld.resetBodyTransform(saved.mesh, saved.position, saved.quaternion);
            }
        });

        // Physics stopped: the sketch overlay and pointer raycasting snap
        // back to identity along with the restored meshes
        this.app.physicsOverlayActive = false;
        this.app.physicsFrameDelta = null;
        if (this.app.overlayFrame) {
            this.app.overlayFrame.position.set(0, 0, 0);
            this.app.overlayFrame.quaternion.identity();
        }

        // ...unless the chair is parked away from its generated pose by the
        // hand tool. That is a staging choice, not simulation state, so it
        // survives Reset - and the overlay goes back to tracking the chair.
        this.app.applyChairMoveOffset();

        console.log(`Restored positions for ${this.app.savedMeshTransforms.length} meshes`);
    }

    /**
     * Reset physics simulation
     */
    resetPhysics() {
        // Reset ragdoll
        if (this.app.ragdoll) {
            this.app.ragdoll.reset();
        }

        // Restore mesh positions to pre-physics state
        this.restorePhysicsPositions();

        // Stop physics simulation
        this.app.isPhysicsPlaying = false;

        console.log('Physics simulation reset - all objects returned to initial positions');
    }

    /**
     * Toggle ragdoll visibility
     * @returns {boolean} New visibility state
     */
    toggleRagdoll() {
        if (this.app.ragdoll) {
            const show = !this.app.ragdoll.visible;
            if (show) {
                // Spawn STANDING on the ground in front of the chair
                this.app.ragdoll.spawnStandingInFront(
                    this.app.generatedMeshes, this.app.physicsWorld?.groundY ?? -5);
                this.app.ragdoll.setVisible(true);
                this.frameFigureAndChair();
            } else {
                this.app.ragdoll.setVisible(false);
                this.app.deselectFigure?.(); // hidden figure can't stay selected
            }
            console.log('Ragdoll visibility:', this.app.ragdoll.visible);
            return this.app.ragdoll.visible;
        }
        return false;
    }

    /**
     * Zoom/pan the camera so the figure (plus the chair, when present)
     * fills most of the view. Bounds are pivot-local (same frame the
     * physics runs in), so the scene-rotation pivot's matrix carries
     * them to world for the camera.
     */
    frameFigureAndChair() {
        const doll = this.app.ragdoll;
        const cam = this.app.cameraController;
        if (!doll || !cam?.zoomToFit) return;

        const fb = doll.getBodyBounds();
        if (!isFinite(fb.minX)) return;
        const box = new THREE.Box3(
            new THREE.Vector3(fb.minX, fb.minY, fb.minZ),
            new THREE.Vector3(fb.maxX, fb.maxY, fb.maxZ));
        box.union(chairLocalBox(this.app.generatedMeshes));

        const pivot = this.app.sceneRotation?.getPivotGroup?.();
        if (pivot) pivot.updateMatrixWorld(true);
        cam.zoomToFit(box, 0.7, pivot ? pivot.matrixWorld : null);
    }

    /**
     * Scale ragdoll size
     * @param {number} factor - Scale factor (e.g., 1.1 for 10% bigger)
     */
    scaleRagdoll(factor) {
        const doll = this.app.ragdoll;
        if (!doll) return;
        // Lower bound 0.3 = the age-proportion blend's full-toddler end
        doll.scale = Math.min(2.5, Math.max(0.3, doll.scale * factor));
        // FULL rebuild (bodies + colliders + joints) at the same spot,
        // feet back on the ground - the old reset() didn't resize the
        // colliders/meshes or joint anchors, so scaling silently broke
        // the figure's connections
        const groundY = this.app.physicsWorld?.groundY ?? -5;
        doll.rebuild(doll.position.x, doll.standingPelvisY(groundY), doll.position.z);
        console.log('Ragdoll scaled by', factor, 'to', doll.scale.toFixed(2),
            `(~${Math.round(doll.getStandingHeight() * 100)}mm tall)`);
    }

    /**
     * Toggle the figure between standing and seated, re-posed in place.
     * @returns {string} the new pose name ('standing' | 'seated')
     */
    toggleFigurePose() {
        const doll = this.app.ragdoll;
        if (!doll) return 'standing';
        const next = doll.currentPose === 'seated' ? 'standing' : 'seated';
        doll.setPose(next, this.app.physicsWorld?.groundY ?? -5);
        return next;
    }
}
